import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSalesDocument, updateSalesDocument } from '../redux/actions/salesDocumentActions';
import { fetchFiles } from '../redux/actions/fileActions';
import { fetchRelationships } from '../redux/actions/relationshipActions';
import { listTimesheets } from '../redux/actions/timesheetActions';
import CreateInvoicePage from './CreateInvoicePage';
import {listDisbursements} from '../redux/actions/disbursementActions';
import { Box, CircularProgress } from '@mui/material';

const ViewEditInvoicePage = () => {
  const { t } = useTranslation(['common', 'pages']);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const { salesDocument, loading, error } = useSelector((state) => state.salesDocumentDetails);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { timesheets } = useSelector((state) => state.timesheetList);
  const { disbursements } = useSelector((state) => state.DisbursementsList || { disbursements: [] });
  const { files } = useSelector((state) => state.fileList);

  // Reset salesDocument when component unmounts
  useEffect(() => {
    return () => {
      dispatch({ type: 'SALESDOCUMENT_DETAILS_RESET' });
      setHasFetched(false);
    };
  }, [dispatch]);

  // Fetch necessary data
  useEffect(() => {
    // Prevent multiple fetches for the same ID
    if (hasFetched && salesDocument?._id === id) {
      return;
    }

    const fetchData = async () => {
      // Only reset if we're fetching a different document
      if (salesDocument?._id !== id) {
        dispatch({ type: 'SALESDOCUMENT_DETAILS_RESET' });
      }
      
      // Always fetch timesheets to ensure we have complete data for filtering
      // Only fetch other data if it doesn't exist in Redux store
      const promises = [];
      promises.push(dispatch(listTimesheets())); // Always fetch timesheets
      if (!files || files.length === 0) {
        promises.push(dispatch(fetchFiles()));
      }
      if (!relationships || relationships.length === 0) {
        promises.push(dispatch(fetchRelationships()));
      }
      if (!disbursements || disbursements.length === 0) {
        promises.push(dispatch(listDisbursements()));
      }

      // Wait for any necessary data fetches
      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Fetch sales document only if we don't already have it
      if (id && salesDocument?._id !== id) {
        await dispatch(getSalesDocument(id));
        setHasFetched(true);
      } else if (salesDocument?._id === id) {
        setHasFetched(true);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  // useEffect(() => {
  //   if (salesDocument && salesDocument?.items && relationships?.length > 0) {
  //     const formatDate = (dateString) => {
  //       if (!dateString) return '';
  //       const date = new Date(dateString);
  //       return date instanceof Date && !isNaN(date) 
  //         ? date.toISOString().split('T')[0]
  //         : '';
  //     };

  //     const expenseRows = [];
  //     const disbursementRows = [];
  //     let initialFeesValue = 0;

  //     if (Array.isArray(salesDocument.items)) {
  //       salesDocument.items.forEach(item => {
  //         if (item.description === "Fees") {
  //           initialFeesValue = Number(item.value);
  //         }
          
  //         // Handle Expenses
  //         if (item.description === "Expenses") {
  //           // Get expenses from both the single expense and expenses array
  //           const allExpenses = [
  //             ...(item.expense ? [item.expense] : []),
  //             ...(Array.isArray(item.expenses) ? item.expenses : [])
  //           ];
            
  //           allExpenses.forEach(expense => {
  //             if (expense) {
  //               expenseRows.push({
  //                 ...expense,
  //                 date: formatDate(expense.date),
  //                 owner: expense.owner || salesDocument.from?._id,
  //                 type: expense.type || "kilometer",
  //                 quantity: Number(expense.quantity) || 1,
  //                 rate: Number(expense.rate) || 0,
  //                 amount: Number(expense.amount) || 0,
  //                 description: expense.description || "",
  //                 isEditable: false
  //               });
  //             }
  //           });
  //         }

  //         // Handle Disbursements
  //         if (item.description === "High Disbursement") {
  //           // Get disbursements from both single disbursement and disbursements array
  //           const allDisbursements = [
  //             ...(item.disbursement ? [item.disbursement] : []),
  //             ...(Array.isArray(item.disbursements) ? item.disbursements : [])
  //           ];

  //           allDisbursements.forEach(disbursement => {
  //             if (disbursement) {
  //               disbursementRows.push({
  //                 ...disbursement,
  //                 date: formatDate(disbursement.date),
  //                 owner: disbursement.owner || salesDocument.from?._id,
  //                 type: disbursement.type || "Other High Disbursement",
  //                 description: disbursement.description || "",
  //                 amount: Number(disbursement.amount) || 0,
  //                 isEditable: false
  //               });
  //             }
  //           });
  //         }
  //       });
  //     }

  //     // Only set default rows if no existing rows were found
  //     const finalExpenseRows = expenseRows.length > 0 ? expenseRows : [];

  //     const finalDisbursementRows = disbursementRows.length > 0 ? disbursementRows : [];

  //     setInitialData({
  //       ...salesDocument,
  //       case: salesDocument.case?._id || '',
  //       from: salesDocument.from?._id || '',
  //       to: salesDocument.to?._id || '',
  //       date: formatDate(salesDocument.date),
  //       dueDate: formatDate(salesDocument.dueDate),
  //       items: salesDocument.items.map(item => ({
  //         ...item,
  //         value: item.description === "Fees" ? initialFeesValue : Number(item.value) || 0,
  //         // Remove single expense/disbursement fields and use arrays only
  //         expense: undefined,
  //         disbursement: undefined,
  //         expenses: item.description === "Expenses" ? finalExpenseRows : [],
  //         disbursements: item.description === "High Disbursement" ? finalDisbursementRows : []
  //       })),
  //       expenseRows: finalExpenseRows,
  //       disbursementRows: finalDisbursementRows,
  //       selectedTimesheets: (timesheets || [])
  //         .filter(ts => ts.fileCase?._id === salesDocument.case?._id && ts.isBilled)
  //         .map(ts => ts._id),
  //       initialFeesValue,
  //       total: Number(salesDocument.total) || 0,
  //       totalDue: Number(salesDocument.totalDue) || 0,
  //       VAT: Number(salesDocument.VAT) || 0
  //     });
  //   }
  // }, [salesDocument, relationships, timesheets]);

  useEffect(() => {
    if (salesDocument && salesDocument?.items && relationships?.length > 0) {
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date) 
          ? date.toISOString().split('T')[0]
          : '';
      };
  
      let initialFeesValue = 0;
  
      if (Array.isArray(salesDocument.items)) {
        salesDocument.items.forEach(item => {
          if (item.description === "Fees") {
            initialFeesValue = Number(item.value);
          }
        });
      }
  
      setInitialData({
        ...salesDocument,
        case: salesDocument.case?._id || '',
        from: salesDocument.from?._id || '',
        to: salesDocument.to?._id || '',
        date: formatDate(salesDocument.date),
        dueDate: formatDate(salesDocument.dueDate),
        items: salesDocument.items.map(item => ({
          ...item,
          value: item.description === "Fees" ? initialFeesValue : Number(item.value) || 0,
          expenses: item.description === "Expenses" ? item.expenses || [] : [],
          disbursements: item.description === "High Disbursement" ? item.disbursements || [] : []
        })),
        selectedTimesheets: (timesheets || [])
          .filter(ts => ts.fileCase?._id === salesDocument.case?._id && ts.isBilled)
          .map(ts => ts._id),
        initialFeesValue,
        total: Number(salesDocument.total) || 0,
        totalDue: Number(salesDocument.totalDue) || 0,
        VAT: Number(salesDocument.VAT) || 0
      });
    }
  }, [salesDocument, relationships, timesheets]);



  if (loading || !initialData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <div>Error: {error}</div>;

  console.log("initialData", initialData);

  return (
    <CreateInvoicePage
      isEditing={true}
      initialData={initialData}
      // onSubmit={(formData) => {
      //   // Format the data before submitting
      //   console.log('Form Data f:', formData);
      //   // const submitData = {
      //   //   ...formData,
      //   //   items: formData.items.map(item => {
      //   //     if (item.description === "Expenses") {
      //   //       return {
      //   //         ...item,
      //   //         expenses: formData.expenseRows
      //   //           .filter(row => !row.isEditable)
      //   //           .map(({ isEditable, ...row }) => row)
      //   //       };
      //   //     }
      //   //     if (item.description === "High Disbursement") {
      //   //       return {
      //   //         ...item,
      //   //         disbursements: formData.disbursementRows
      //   //           .filter(row => !row.isEditable)
      //   //           .map(({ isEditable, ...row }) => row)
      //   //       };
      //   //     }
      //   //     return item;
      //   //   })
      //   // };
      //   // const submitData = {
      //   //   ...formData,
      //   //   items: formData.items.map(item => {
      //   //     if (item.description === "Expenses") {
      //   //       return {
      //   //         ...item,
      //   //         expenses: [
      //   //           // Preserve existing expenses
      //   //           ...(item.expenses || []),
      //   //           // Add new finalized expenses from expenseRows
      //   //           ...formData.expenseRows
      //   //             .filter(row => !row.isEditable)
      //   //             .map(({ isEditable, ...row }) => row)
      //   //         ]
      //   //       };
      //   //     }
      //   //     if (item.description === "High Disbursement") {
      //   //       return {
      //   //         ...item,
      //   //         disbursements: [
      //   //           // Preserve existing disbursements
      //   //           ...(item.disbursements || []),
      //   //           // Add new finalized disbursements from disbursementRows
      //   //           ...formData.disbursementRows
      //   //             .filter(row => !row.isEditable)
      //   //             .map(({ isEditable, ...row }) => row)
      //   //         ]
      //   //       };
      //   //     }
      //   //     return item; // Return other items unchanged
      //   //   })
      //   // };
      //   // console.log('Submitting:', submitData);
      //   dispatch(updateSalesDocument(id, formData));
      //   setTimeout(() => {
      //     navigate(-1);
      //   }, 2000);
      // }}

      onSubmit={(formData) => {
        const submitData = {
          ...formData,
          items: formData.items.map(item => {
            if (item.description === "High Disbursement") {
              return {
                ...item,
                disbursements: item.disbursements.map(disb => disb._id) // Submit only _id references
              };
            }
            return item;
          })
        };
      
        console.log('Submitting:', submitData);
        dispatch(updateSalesDocument(id, submitData));
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }}
    />
  );
};

export default ViewEditInvoicePage;
